import React, { useEffect, useMemo, memo } from 'react';
import {
  ResponsiveContainer,
  StackedCarousel,
} from 'react-stacked-center-carousel';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { Empty, Image, Skeleton } from 'antd';
import { isEmpty } from 'lodash';
import Icons from '../../../configs/icon.config';
import { Carousel, CustomCard } from '../index';
import './styles.scss';
import { useDashBoardImagesQuery } from '../../../view/dashboard/store/dashboardSlice';

interface Props {
  data: any[]; // Provide the correct type for the 'data' property
  dataIndex: number; // Provide the correct type for the 'dataIndex' property
}

// Very import to memoize your Slide component otherwise there might be performance issue
// At minimum your should do a simple React.memo(SlideComponent)
// If you want the absolute best performance then pass in a custom comparator function like below
// eslint-disable-next-line react/display-name
export const Card = React.memo((props: Props) => {
  const { data, dataIndex } = props;
  const { image } = data[dataIndex];
  return (
    <div
      style={{
        width: '100%',
        userSelect: 'none',
      }}
      className="my-slide-component"
    >
      <Image
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'cover',
          borderRadius: 0,
        }}
        draggable={false}
        src={image}
      />
    </div>
  );
});

function ResponsiveCarousel() {
  const ref = React.useRef<any>();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { t } = useTranslation();

  const {
    data: sliderData,
    isLoading: sliderLoading,
    isError,
  } = useDashBoardImagesQuery();

  const data = useMemo(() => {
    return (
      sliderData?.dashboard_sliders?.map((result: any) => ({
        image: result.image,
        alt_text: result.alt_text,
      })) || []
    );
  }, [sliderData?.dashboard_sliders]);

  useEffect(() => {
    const interval = setInterval(() => {
      ref.current?.goNext();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  if (isError)
    return (
      <div className="pt-10">
        <CustomCard
          type="figure"
          isBlank
          styles={{ width: '100%', height: 400 }}
          component={
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('placeholder.slider')}
            />
          }
        />
      </div>
    );

  const carousel = () => {
    if (isEmpty(sliderData?.dashboard_sliders)) {
      return (
        <div className="dark:bg-[#1D1D1D] bg-white rounded-2xl h-full relative  py-[2rem] px-[2.5rem] shadow-2xl mt-[20px]">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('placeholder.slider')}
          />
        </div>
      );
    }
    if (
      sliderData?.dashboard_sliders &&
      sliderData?.dashboard_sliders?.length < 3
    ) {
      return <Carousel imageLinks={sliderData?.dashboard_sliders} />;
    }
    return (
      <div style={{ width: '100%', position: 'relative' }}>
        <ResponsiveContainer
          carouselRef={ref}
          render={(parentWidth, carouselRef) => {
            // If you want to use a ref to call the method of StackedCarousel, you cannot set the ref directly on the carousel component
            // This is because ResponsiveContainer will not render the carousel before its parent's width is determined
            // parentWidth is determined after your parent component mounts. Thus if you set the ref directly it will not work since the carousel is not rendered
            // Thus you need to pass your ref object to the ResponsiveContainer as the carouselRef prop and in your render function you will receive this ref object
            let currentVisibleSlide = 5;
            if (parentWidth <= 1440) currentVisibleSlide = 3;
            if (parentWidth <= 1080) currentVisibleSlide = 5;
            return (
              <StackedCarousel
                ref={carouselRef}
                slideComponent={Card}
                height={isMobile ? 163 : 420}
                slideWidth={parentWidth < 800 ? parentWidth - 40 : 750}
                carouselWidth={parentWidth}
                data={data}
                currentVisibleSlide={currentVisibleSlide}
                maxVisibleSlide={5}
                disableSwipe
                className="mt-10"
              />
            );
          }}
        />
        <div>
          <button
            style={{ position: 'absolute', left: 20, zIndex: 3 }}
            color="primary"
            onClick={() => {
              ref.current?.goBack();
            }}
            className="bg-gray-300 text-2xl rounded-full p-2 top-[50%] translate-y-[-50%]"
          >
            {Icons.chevronLeft}
          </button>
          <button
            style={{ position: 'absolute', right: 20, zIndex: 3 }}
            color="primary"
            onClick={() => {
              ref.current?.goNext(6);
            }}
            className="bg-gray-300 text-2xl rounded-full p-2 top-[50%] translate-y-[-50%]"
            id="button"
          >
            {Icons.chevronRight}
          </button>
        </div>
      </div>
    );
  };

  // @ts-ignore
  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <div className="news-carousel-wrapper">
      {sliderLoading ? (
        <div
          style={{ width: '100%', position: 'relative', paddingTop: 20 }}
          className="flex items-center justify-center skeleton-wrapper"
        >
          <Skeleton.Image style={{ width: '100%', height: '300px' }} active />
        </div>
      ) : (
        <div>{carousel()}</div>
      )}
    </div>
  );
}

export default memo(ResponsiveCarousel);
